import React, { useContext, useState } from "react";

import {
  Box,
  FormControlLabel,
  Grid,
  LinearProgress,
  Switch,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import {
  DataGrid,
  GridColDef,
  GridToolbarContainer,
  GridToolbarExport,
} from "@mui/x-data-grid";
import { Clipboard } from "react-feather";
import { Link, useParams } from "react-router-dom";
import mainStyles from "styles";

import { UrlParamTypes } from "../../types/org-display";
import GenericDialog from "components/shared/generic-dialog";
import { DocPrefix } from "constants/index";
import { SnackbarContext } from "context/snackbar-context";
import { toS12ID, toUUID, validateUUID } from "helpers/s12-helpers";
import { BulkRowDataTable } from "pages/org-sites";

function CustomExportToolbar() {
  return (
    <GridToolbarContainer>
      <GridToolbarExport
        // Removes printOptions as default toolbar option
        printOptions={{ disableToolbarButton: true }}
      />
    </GridToolbarContainer>
  );
}

function CustomNoRowsOverlay() {
  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      height="100%"
    >
      <Box sx={{ mt: 1 }}>No results found.</Box>
    </Box>
  );
}

const OrganisationSitesTable = ({
  sitesList,
  isLoading,
  includeDeleted,
  handleIncludeDeletedToggle,
}: {
  sitesList: BulkRowDataTable[];
  isLoading: boolean;
  includeDeleted: boolean;
  handleIncludeDeletedToggle: (includeDeleted: boolean) => void;
}) => {
  const theme = useTheme();
  const [pageSize, setPageSize] = React.useState<number>(100);
  // 100 rows is the maximum number of rows that can be rendered for MIT version of Data Grid
  const { copyToClipboard } = useContext(SnackbarContext);
  const classes = mainStyles();
  const { regionCode } = useParams<UrlParamTypes>();

  const [searchText, setSearchText] = useState("");

  const [openUsersDialog, setOpenUsersDialog] = useState(false);
  const [selectedSite, setSelectedSite] = useState<BulkRowDataTable | null>(
    null
  );

  const [includeUserIDs, setIncludeUserIDs] = useState(false);
  const handleIncludeUserIDsToggle = (checked: boolean) => {
    setIncludeUserIDs(!checked);
  };

  const handleSearchChange = (value: string) => {
    setSearchText(value);
  };

  const filteredRows = searchText
    ? sitesList.filter((row) =>
        Object.values(row).some(
          (value) =>
            String(value).toLowerCase().includes(searchText.toLowerCase()) ||
            (validateUUID(searchText) &&
              String(value)
                .toLowerCase()
                .includes(toUUID(searchText.toLowerCase())))
        )
      )
    : sitesList;

  const baseColumns: GridColDef[] = [
    {
      field: "country",
      headerName: "Country",
      width: 180,
      renderCell({ row }) {
        const countryValue =
          row.hierarchy && row.hierarchy.length > 4 ? row.hierarchy[4] : "";
        return (
          <Box
            textAlign="left"
            sx={{
              color: row.deleted
                ? theme.palette.error.main
                : theme.palette.text.primary,
              fontWeight: row.deleted ? "bold" : "normal",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: 160,
              display: "block",
            }}
            title={countryValue}
          >
            {countryValue}
          </Box>
        );
      },
    },
    {
      field: "state",
      headerName: "State",
      width: 180,
      renderCell({ row }) {
        const stateValue =
          row.hierarchy && row.hierarchy.length > 3 ? row.hierarchy[3] : "";
        return (
          <Box
            textAlign="left"
            sx={{
              color: row.deleted
                ? theme.palette.error.main
                : theme.palette.text.primary,
              fontWeight: row.deleted ? "bold" : "normal",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: 160,
              display: "block",
            }}
            title={stateValue}
          >
            {stateValue}
          </Box>
        );
      },
    },
    {
      field: "region",
      headerName: "Region",
      width: 180,
      renderCell({ row }) {
        const regionValue =
          row.hierarchy && row.hierarchy.length > 2 ? row.hierarchy[2] : "";
        return (
          <Box
            textAlign="left"
            sx={{
              color: row.deleted
                ? theme.palette.error.main
                : theme.palette.text.primary,
              fontWeight: row.deleted ? "bold" : "normal",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: 160,
              display: "block",
            }}
            title={regionValue}
          >
            {regionValue}
          </Box>
        );
      },
    },
    {
      field: "area",
      headerName: "Area",
      width: 180,
      renderCell({ row }) {
        const areaValue =
          row.hierarchy && row.hierarchy.length > 1 ? row.hierarchy[1] : "";
        return (
          <Box
            textAlign="left"
            sx={{
              color: row.deleted
                ? theme.palette.error.main
                : theme.palette.text.primary,
              fontWeight: row.deleted ? "bold" : "normal",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: 160,
              display: "block",
            }}
            title={areaValue}
          >
            {areaValue}
          </Box>
        );
      },
    },
    {
      field: "site",
      headerName: "Site",
      width: 180,
      renderCell({ row }) {
        const siteValue =
          row.hierarchy && row.hierarchy.length > 0 ? row.hierarchy[0] : "";
        return (
          <Box
            textAlign="left"
            sx={{
              color: row.deleted
                ? theme.palette.error.main
                : theme.palette.text.primary,
              fontWeight: row.deleted ? "bold" : "normal",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: 160,
              display: "block",
            }}
            title={siteValue}
          >
            {siteValue}
          </Box>
        );
      },
    },
    {
      field: "memberId",
      headerName: "User Member Count",
      width: 180,
      valueGetter: (params) => params.value?.length,
      renderCell({ row }) {
        return (
          <Box
            textAlign="left"
            display="flex"
            sx={{
              ...classes.clickable,
              color: row.deleted
                ? theme.palette.error.main
                : theme.palette.text.primary,
              fontWeight: row.deleted ? "bold" : "normal",
            }}
            onClick={() => {
              setSelectedSite(row);
              setOpenUsersDialog(true);
            }}
          >
            {row.memberId ? row.memberId.length : ""}
            {row.memberId.length > 0 ? (
              <Box sx={{ marginLeft: 2, color: theme.palette.primary.main }}>
                View Users
              </Box>
            ) : null}
          </Box>
        );
      },
    },
    {
      field: "folderId",
      headerName: "Site_ID",
      width: 350,
      renderCell({ row }) {
        return (
          <Typography
            variant="body2"
            align="left"
            onClick={() => copyToClipboard(row.folderId)}
            sx={{
              ...classes.clickable,
              color: row.deleted
                ? theme.palette.error.main
                : theme.palette.text.primary,
              fontWeight: row.deleted ? "bold" : "normal",
            }}
          >
            {row.folderId}{" "}
            <Clipboard
              color={theme.palette.text.primary}
              size={"1rem"}
              className="position-icon"
              data-testid="site-id-copy-to-clipboard"
            />
          </Typography>
        );
      },
    },
    {
      field: "deleted",
      headerName: "Deleted?",
      width: 180,
      renderCell({ row }) {
        return (
          <Box
            textAlign="left"
            sx={{
              color: row.deleted
                ? theme.palette.error.main
                : theme.palette.text.primary,
              fontWeight: row.deleted ? "bold" : "normal",
            }}
          >
            {row.deleted ? "True" : "False"}
          </Box>
        );
      },
    },
  ];

  let columns: GridColDef[];
  if (includeUserIDs) {
    const memberIdx = baseColumns.findIndex((col) => col.field === "memberId");
    columns = [
      ...baseColumns.slice(0, memberIdx + 1),
      {
        field: "userIds",
        headerName: "User IDs",
        width: 220,
        renderCell: ({ row }) => (
          <Box
            textAlign="left"
            sx={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: 180,
              display: "block",
            }}
            title={Array.isArray(row.memberId) ? row.memberId.join(", ") : ""}
          >
            {Array.isArray(row.memberId) ? row.memberId.join(", ") : ""}
          </Box>
        ),
        valueFormatter: (params) =>
          Array.isArray(params.value)
            ? params.value.join(", ")
            : params.value || "",
      },
      ...baseColumns.slice(memberIdx + 1),
    ];
  } else {
    columns = baseColumns;
  }

  const userDialogColumns: GridColDef[] = [
    {
      field: "id",
      headerName: "UserID",
      flex: 1,
      renderCell({ row }) {
        return (
          <Box display="flex" justifyContent="space-between" width="100%">
            <Box
              sx={classes.clickable}
              onClick={() => copyToClipboard(row.id)}
              textAlign="left"
            >
              {row.id}{" "}
              <Clipboard
                color={theme.palette.text.primary}
                size={"1rem"}
                className="position-icon"
              />
            </Box>

            <Link
              to={{
                pathname: `/user/${regionCode}/${toS12ID(
                  DocPrefix.PrefixUser,
                  row.id
                )}`,
              }}
              className="remove-link-style"
              data-testid="user-details-link"
            >
              <Box
                marginRight={5}
                sx={{ ...classes.clickable, color: theme.palette.primary.main }}
                onClick={() => {}}
              >
                Go to User Details
              </Box>
            </Link>
          </Box>
        );
      },
    },
  ];

  return (
    <>
      <Box sx={{ maxHeight: "15vh", width: "100%" }} marginBottom={0}>
        <Grid
          display="flex"
          justifyContent="space-between"
          marginBottom={1}
          gap={2}
        >
          <TextField
            value={searchText}
            onChange={(e) => handleSearchChange(e.target.value)}
            label="Search..."
            disabled={isLoading}
            data-testid="org-sites-search-field"
            sx={{ width: "85%" }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={includeDeleted}
                onChange={() => handleIncludeDeletedToggle(includeDeleted)}
                name="checkedB"
                color="primary"
                data-testid="include-deleted-toggle-switch"
                disabled={isLoading}
              />
            }
            label="Include Deleted"
          />
          <FormControlLabel
            control={
              <Switch
                checked={includeUserIDs}
                onChange={() => handleIncludeUserIDsToggle(includeUserIDs)}
                name="includeUserIDs"
                color="primary"
                data-testid="include-userids-toggle-switch"
                disabled={isLoading}
              />
            }
            label="Include UserIDs"
          />
        </Grid>
      </Box>
      <Box sx={{ height: "70vh", width: "100%" }}>
        <DataGrid
          rows={filteredRows}
          columns={columns}
          paginationModel={{ page: 0, pageSize: pageSize }}
          onPaginationModelChange={(model) => setPageSize(model.pageSize)}
          pageSizeOptions={[25, 50, 100]}
          getRowId={(row) => row.folderId}
          getRowHeight={() => "auto"}
          disableRowSelectionOnClick
          components={{
            LoadingOverlay: LinearProgress,
            NoRowsOverlay: isLoading ? LinearProgress : CustomNoRowsOverlay,
            Toolbar: CustomExportToolbar,
          }}
          data-testid="org-sensor-table"
          disableVirtualization
          loading={isLoading}
          sortingOrder={["asc", "desc"]}
          initialState={{
            sorting: {
              sortModel: [{ field: "location", sort: "asc" }],
            },
            pagination: {
              paginationModel: { pageSize: 100 },
            },
          }}
          sx={{
            // Removes the default cell border on click
            "& .MuiDataGrid-cell:focus": {
              outline: "none",
            },
          }}
        />
      </Box>
      <GenericDialog
        title={
          (selectedSite ? selectedSite.name + " - " : "") + "Site User Members"
        }
        width="md"
        hasAckBtn={true}
        acknowledgeBtnLabel="OK"
        open={openUsersDialog}
        handleAckBtnClick={() => {
          setOpenUsersDialog(false);
        }}
        handleDialogClose={() => {
          setOpenUsersDialog(true);
        }}
        data-testid="permission-set-details-dialog"
      >
        <Box sx={{ height: "70vh", width: "100%" }}>
          <DataGrid
            rows={
              selectedSite ? selectedSite.memberId.map((id) => ({ id })) : []
            }
            columns={userDialogColumns}
            density="compact"
            getRowId={(row) => row.id}
            components={{
              Toolbar: CustomExportToolbar,
            }}
            disableRowSelectionOnClick
            pageSizeOptions={[25, 50, 100]}
          ></DataGrid>
        </Box>
      </GenericDialog>
    </>
  );
};

export default OrganisationSitesTable;
